import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { TippyTooltipDirective } from '../../directives/tippy-tooltip.directive';
import {
  WizardStepStatusEnum,
  type WizardStepperBadgeIcon,
  type WizardStepperItem,
  type WizardStepperView,
  type WizardStepStatus,
} from './wizard.types';

/** Statuswoord in de aria-label; een schermlezer ziet het vinkje/streepje/nummer niet. */
const STATUS_LABELS: Record<WizardStepStatus, string> = {
  [WizardStepStatusEnum.NotStarted]: 'niet gestart',
  [WizardStepStatusEnum.Incomplete]: 'onvolledig',
  [WizardStepStatusEnum.Complete]: 'volledig',
};

/**
 * Positional model: the badge encodes POSITION, not data, so it must not borrow the data words. A
 * step after the current one has not been reached — saying "niet gestart" would claim knowledge
 * about its contents that this model does not have.
 */
const POSITIONAL_LABELS: Record<WizardStepStatus, string> = {
  [WizardStepStatusEnum.NotStarted]: 'nog te doen',
  // Unreachable by construction — the positional branch only ever derives Complete or NotStarted.
  // Present to satisfy the Record type, not because anything can produce it.
  [WizardStepStatusEnum.Incomplete]: 'nog te doen',
  [WizardStepStatusEnum.Complete]: 'afgerond',
};

/**
 * Custom wizard-stepper header (Figma "Interactive wizard steps", node 2353:36776) that replaces
 * Angular Material's horizontal stepper header. A centered row of atoms (20px badge + label)
 * joined by 32px connector lines.
 *
 * The component renders two models, chosen by whether the host supplies `statuses`:
 *
 * 1. **Data-driven** (`statuses` non-empty) — the interactive/non-linear flow the Figma design
 *    specifies. Each step carries one of three statuses derived from what the user actually
 *    filled in, and `active` is a COLOUR OVERLAY on top of it that never replaces the glyph:
 *
 *    | status        | badge                                   | active badge          |
 *    |---------------|-----------------------------------------|-----------------------|
 *    | `not-started` | transparent, 1px ring, step number      | filled, step number   |
 *    | `incomplete`  | filled, dashed-ring glyph               | filled, dashed glyph  |
 *    | `complete`    | filled, check glyph                     | filled, check glyph   |
 *
 *    Nothing is ever locked: every step stays clickable regardless of status, and clicking one
 *    makes it active while the previously active step falls back to its own real status.
 *
 * 2. **Positional** (`statuses` empty) — the linear wizard and the read-only progress indicators
 *    (mobile, submitted-onboarding screen), unchanged: steps before the current one read as
 *    complete, the current one shows its number, later ones show a hollow ring. Reachability
 *    still gates clicks here, because a linear flow genuinely can lock a step. A locked step is
 *    muted and carries `aria-disabled`, so the lock is visible BEFORE the click rather than only as
 *    a click that does nothing (#21292). It keeps its number: the row must still read 1, 2, 3…
 *
 * Connector lines are flat grey in BOTH models and never encode progress — with steps completable
 * in any order a coloured segment would misrepresent overall progress.
 *
 * Hover and keyboard focus draw a 1px frame around the whole step (Border/Secondary on hover,
 * Border/Dark on focus). Deliberately NOT an underline — note that the Figma CANVAS annotation
 * still reads "Hover shows an underline on the label"; the linked Notion engineering handover
 * supersedes it with "No underline on the text — the border is the only hover affordance". Follow
 * the handover, and do not re-add the underline when the canvas text says otherwise.
 *
 * Set `compact` (mobile) to drop the labels and tighten the connectors. Without it the row condenses
 * on its own once the full row no longer fits: the inactive steps fold to their badge (label in the
 * tooltip and aria-label), so no step is ever clipped out of view.
 */
@Component({
  selector: 'app-wizard-stepper',
  imports: [MatIconModule, TippyTooltipDirective],
  template: `
    <!--
      role=list / listitem rather than a plain group: the aria-label replaces the button's text
      content, so the visible step number never reaches a screen reader. List semantics hand back
      "1 van 4" without stuffing the ordinal into every label.
    -->
    <div
      #row
      class="wizard-stepper"
      role="list"
      aria-label="Stappen"
      [class.wizard-stepper--compact]="compact()"
      [class.wizard-stepper--condensed]="condensed()"
      [class.wizard-stepper--static]="!interactive()">
      @for (view of stepViews(); track view.index) {
        <!--
          Connector + step travel together in one flex item, so a connector always belongs to the
          step it leads into and can never be stranded at the END of a wrapped line. It does mean a
          wrapped line begins with a connector; the row is designed as a single line and the
          connector is decorative (aria-hidden), so that is the lesser artefact.
        -->
        <!--
          The notice tooltip hangs off the CELL, not off the step button or the dot inside it. A
          disabled button dispatches no user-generated mouse events and takes no focus, so a tooltip
          anchored anywhere inside one can never open — and a locked step is exactly where the user
          most needs to read why. tippyTooltipAria is off because the notice is already part of the
          button's accessible name; leaving it on makes a screen reader read the same sentence twice.
        -->
        <span
          class="wizard-stepper__cell"
          role="listitem"
          [tippyTooltip]="view.notice ?? (condensed() && !view.active ? view.label : null)"
          [tippyTooltipAria]="false">
          @if (!$first) {
            <span class="wizard-stepper__line" aria-hidden="true"></span>
          }
          <button
            type="button"
            class="wizard-stepper__step"
            [attr.data-cy]="'wizard-step-' + view.index"
            [attr.data-status]="view.status"
            [disabled]="!view.clickable"
            [attr.aria-disabled]="view.clickable ? null : 'true'"
            [attr.aria-current]="view.active ? 'step' : null"
            [attr.aria-label]="view.ariaLabel"
            (click)="onStepClick(view)">
            <span [class]="atomClass(view)">
              @switch (view.icon) {
                @case ('check') {
                  <mat-icon class="wizard-stepper__glyph icon-size-3" svgIcon="lucide:check" />
                }
                @case ('dashed') {
                  <mat-icon class="wizard-stepper__glyph icon-size-3" svgIcon="lucide:circle-dashed" />
                }
                @default {
                  {{ view.index + 1 }}
                }
              }
            </span>
            @if (!compact() && (!condensed() || view.active)) {
              <span [class]="labelClass(view)">{{ view.label }}</span>
            }
            <!--
              The step carries a notice. Purely decorative here: the text itself lives in the
              button's aria-label and in the cell's tooltip, both of which a locked step keeps.
            -->
            @if (view.notice) {
              <span class="wizard-stepper__flag" data-cy="wizard-step-flag" aria-hidden="true"></span>
            }
          </button>
        </span>
      }
    </div>
  `,
  styleUrl: './wizard-stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WizardStepperComponent {
  /** Step labels in visual order (form steps first, an optional review step last). */
  readonly steps = input.required<WizardStepperItem[]>();

  /** Index of the currently active step. */
  readonly currentIndex = input.required<number>();

  /**
   * Per-step underlying status. Supplying it switches the header to the data-driven model from the
   * Figma design; leaving it empty keeps the positional model the linear wizard has always used.
   */
  readonly statuses = input<WizardStepStatus[]>([]);

  /** Per-step reachability; missing/short entries default to reachable. Positional model only. */
  readonly reachable = input<boolean[]>([]);

  /** Drop the labels and tighten the connectors — used as a compact mobile progress indicator. */
  readonly compact = input(false);

  /** Whether steps emit `stepClick`; false renders a display-only progress indicator. */
  readonly interactive = input(true);

  /**
   * Notice text per step, index-aligned with {@link steps}: a non-null entry marks that step and is
   * rendered VERBATIM as its tooltip and in its aria-label. Missing or short entries are unmarked.
   * The host owns the wording — this component adds no lead-in of its own.
   */
  readonly notices = input<Array<string | null>>([]);

  /** Emits the index of a clicked, clickable step. */
  readonly stepClick = output<number>();

  private readonly row = viewChild.required<ElementRef<HTMLElement>>('row');

  /** The full row overflows its container, so only the active step keeps its label. */
  protected readonly condensed = signal(false);

  /** Width of the full (uncondensed) row; only measurable while it is rendered uncondensed. */
  #fullWidth = 0;

  constructor() {
    // New labels or another active step change the full width: start from the full row again.
    effect(() => {
      this.stepViews();
      untracked(() => this.condensed.set(false));
    });
    afterRenderEffect(() => {
      this.stepViews();
      this.condensed();
      this.#fit();
    });
    const observer = new ResizeObserver(() => this.#fit());
    // The row keeps its width when only its content grows (e.g. a late web font widens the labels),
    // so the cells are observed too; re-observing an element is a no-op.
    afterRenderEffect(() => {
      this.stepViews();
      const row = this.row().nativeElement;
      for (const el of [row, ...Array.from(row.children)]) {
        observer.observe(el);
      }
    });
    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }

  #fit(): void {
    const row = this.row().nativeElement;
    if (!this.condensed()) {
      this.#fullWidth = row.scrollWidth;
    }
    this.condensed.set(!this.compact() && this.#fullWidth > row.clientWidth);
  }

  protected readonly stepViews = computed<WizardStepperView[]>(() => {
    const current = this.currentIndex();
    const reach = this.reachable();
    const statuses = this.statuses();
    const canNavigate = this.interactive();
    const notices = this.notices();
    const dataDriven = statuses.length > 0;

    return this.steps().map((step, index) => {
      const active = index === current;
      const status: WizardStepStatus = dataDriven
        ? (statuses[index] ?? WizardStepStatusEnum.NotStarted)
        : index < current
          ? WizardStepStatusEnum.Complete
          : WizardStepStatusEnum.NotStarted;

      const notice = notices[index] ?? null;
      /*
        Locked by the linear flow, and only there: the data-driven model never locks anything, the
        display-only indicator disables every step without any of them being unreachable, and the
        step the user is standing on is never locked out of itself (see `clickable` below).
      */
      const blocked = canNavigate && !dataDriven && !active && !(reach[index] ?? true);

      return {
        index,
        label: step.label,
        notice,
        status,
        active,
        blocked,
        icon: this.#badgeIcon(status, active, dataDriven),
        /*
          Data-driven: nothing is ever locked. Positional: the linear flow's gating still applies,
          except to the step the user is standing on — you are never locked out of where you already
          are. Reachability is value-driven, so without that exemption a value entered on step 3
          could retro-lock step 1 and disable step 3 underneath the user, dropping it out of the tab
          order. The matching :not(...--active) CSS guards stay as belt-and-braces.
        */
        clickable: canNavigate && (dataDriven || active || (reach[index] ?? true)),
        // The lock and the notice ride along in the label because the muted colour
        // and the dot are visual cues a screen reader would otherwise miss entirely. Both are
        // suffixes rather than replacements: a completed step that a later value retro-locked is
        // still completed, and hiding that would tell the user to redo work they already did. The
        // notice is carried in full — a locked step cannot be focused, so its aria-label is the only
        // place assistive tech can still reach the reason.
        ariaLabel: `${step.label}, ${this.#stateWord(status, active, dataDriven, canNavigate)}${blocked ? ', niet beschikbaar' : ''}${notice ? `, ${notice}` : ''}`,
      };
    });
  });

  /**
   * The word after the step name in the aria-label — a screen reader sees neither the glyph nor the
   * colour.
   *
   * Data-driven: always the REAL status, never "actief". Active is a colour overlay, not a fourth
   * status, and swapping it in would rob the one step the user is standing on of its status; being
   * current is carried structurally by `aria-current="step"`.
   *
   * Positional: the status is fabricated from the index, so the data words would lie — the active
   * step would announce "niet gestart" while the user is filling it in. Here "actief" IS the honest
   * answer, but ONLY while the stepper is interactive. A display-only indicator (the submitted
   * onboarding header) is a read-out where nobody is standing anywhere, so it falls back to the
   * position word, which is also what its badge actually renders.
   */
  #stateWord(status: WizardStepStatus, active: boolean, dataDriven: boolean, interactive: boolean): string {
    if (dataDriven) {
      return STATUS_LABELS[status];
    }
    return active && interactive ? 'actief' : POSITIONAL_LABELS[status];
  }

  /**
   * In the data-driven model the glyph follows the real status and `active` only recolours it. The
   * positional model keeps its old rule: the step you are standing on shows its number, so the
   * header still reads as "you are at 3 of 5" rather than as a completion summary.
   */
  #badgeIcon(
    status: WizardStepStatus,
    active: boolean,
    dataDriven: boolean
  ): WizardStepperBadgeIcon {
    if (active && !dataDriven) {
      return 'number';
    }
    if (status === WizardStepStatusEnum.Complete) {
      return 'check';
    }
    return status === WizardStepStatusEnum.Incomplete ? 'dashed' : 'number';
  }

  protected atomClass(view: WizardStepperView): string {
    const active = view.active ? ' wizard-stepper__atom--active' : '';
    return `wizard-stepper__atom wizard-stepper__atom--${view.status}${active}`;
  }

  protected labelClass(view: WizardStepperView): string {
    return view.active ? 'wizard-stepper__label wizard-stepper__label--active' : 'wizard-stepper__label';
  }

  protected onStepClick(view: WizardStepperView): void {
    if (view.clickable) {
      this.stepClick.emit(view.index);
    }
  }
}
